/**
 * Investor-Portal — Segment CARRIER / PORTO-TRANSPORT (#1178 v2.564.0, revidiert #1789).
 *
 * Handelsmarge — KEIN Personal, KEIN Finecom-RevShare.
 * Segment = KSt [2, 18, 20] UND freigegebener Kontenkreis (Mapping), nur gebuchte Belege.
 * Marge = Verkauf (invoice) − Einkauf (vendor_bill + other).
 *
 * Layout:
 *   - PeriodToggle
 *   - 4 KPI-Tiles (Verkauf / Einkauf / Marge / Marge % — inkl. Marge ohne Zoll-Durchlauf)
 *   - Ströme-Tabelle: Paket / Zoll-Durchlauf / Spedition getrennt (#1789), Zoll aufklappbar (#1791)
 *   - Zoll-Δ-Prüfung je Monat mit Ampel + Kumulativwert + Klickpfad auf die Belege (#1791)
 *   - AT/DE/Σ-Verkaufs-Tabelle
 *   - Stück-KPIs: Verkauf/Einkauf/Marge pro Sendung — NUR Paketstrom (KS 2)
 *   - Top-5 Carrier (Fußzeile weist nicht zuordenbare Abgrenzungen aus)
 *   - Monats-Trend
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Wallet, BarChart3, Percent, ChevronRight, ChevronDown, ExternalLink, X } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { MaturityOverlay, reifeFooter } from './components/MaturityChartOverlay';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(v); }
function fmtEurDetail(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:2}).format(v); }
function fmtEurCent(v) { if (v==null||isNaN(v)) return '—'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',minimumFractionDigits:2,maximumFractionDigits:2}).format(v); }
function fmtPct(v) { if (v==null||isNaN(v)) return '–'; return `${v.toFixed(1)} %`; }
function fmtInt(v) { if (v==null||isNaN(v)) return '—'; return new Intl.NumberFormat('de-AT',{maximumFractionDigits:0}).format(v); }

// #1791 Δ-Ampel — Schwellen kommen aus dem Backend (aus der gemessenen Streuung hergeleitet).
const AMPEL_FARBE = { gruen: '#10B981', gelb: '#F59E0B', rot: '#EF4444' };
function AmpelDot({ ampel, title }) {
  if (!ampel) return null;
  return (
    <span title={title} aria-label={`Ampel ${ampel}`}
      className="inline-block w-2.5 h-2.5 rounded-full align-middle shrink-0"
      style={{ backgroundColor: AMPEL_FARBE[ampel] || '#9CA3AF' }} />
  );
}

export default function InvestorSegmentCarrier() {
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  const [periodState, setPeriodState] = useState(() => {
    // #1184 v2.569.0 — Default = letzter belastbarer Monat
    const mature = defaultMatureMonthState();
    return {
      period: searchParams.get('period') || 'month',
      year: parseInt(searchParams.get('year') || mature.year, 10),
      month: parseInt(searchParams.get('month') || mature.month, 10),
      quarter: parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10),
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [yearTrend, setYearTrend] = useState(null);
  const [yearTrendLoading, setYearTrendLoading] = useState(true);
  const [yearTrendError, setYearTrendError] = useState(null);

  // #1791 — aufklappbare Zoll-Zeile + Klickpfad auf die Belege eines Monats
  const [zollOpen, setZollOpen] = useState(false);
  const [belegeMonat, setBelegeMonat] = useState(null);
  const [belege, setBelege] = useState(null);
  const [belegeLoading, setBelegeLoading] = useState(false);
  const [belegeError, setBelegeError] = useState(null);

  // #1184 v2.569.0 — YTD frontend-seitig auf belastbaren Cutoff verkürzen
  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/segmente/carrier?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setData(null); } else setData(json);
      } catch (e) { if (!abort) setError(e.message); }
      finally { if (!abort) setLoading(false); }
    }
    load(); return () => { abort = true; };
  }, [periodQuery]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setYearTrendLoading(true); setYearTrendError(null);
      try {
        const res = await fetch(`${API}/api/investor/segmente/carrier/jahrestrend?year=${periodState.year}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setYearTrendError(json.error || `HTTP ${res.status}`); setYearTrend(null); } else setYearTrend(json);
      } catch (e) { if (!abort) setYearTrendError(e.message); }
      finally { if (!abort) setYearTrendLoading(false); }
    }
    load(); return () => { abort = true; };
  }, [periodState.year]);

  useEffect(() => {
    if (belegeMonat == null) { setBelege(null); setBelegeError(null); return; }
    let abort = false;
    (async () => {
      setBelegeLoading(true); setBelegeError(null);
      try {
        const res = await fetch(
          `${API}/api/investor/segmente/carrier/zoll-belege?year=${periodState.year}&monat=${belegeMonat}`,
          { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setBelegeError(json.error || `HTTP ${res.status}`); setBelege(null); } else setBelege(json);
      } catch (e) { if (!abort) setBelegeError(e.message); }
      finally { if (!abort) setBelegeLoading(false); }
    })();
    return () => { abort = true; };
  }, [belegeMonat, periodState.year]);

  const chartData = useMemo(() => {
    if (!yearTrend?.monats_trend) return [];
    return yearTrend.monats_trend.map(m => ({
      monat: MONTH_LABELS[m.monat - 1],
      Verkauf: Math.round(m.umsatz || 0),
      Einkauf: Math.round(m.aufwand || 0),
      Marge: Math.round(m.db || 0),
    }));
  }, [yearTrend]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Segment Porto &amp; Transport</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Handelsmarge auf Versand-Dienstleistungen — Verkauf an Kunden minus Einkauf bei Carriern.
            Keine eigenen Personal- oder RevShare-Aufwände.
          </div>
        </div>
        <PeriodToggle
          period={periodState.period} year={periodState.year} month={periodState.month}
          quarter={periodState.quarter} fromDate={periodState.fromDate} toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      {/* #1184 v2.569.0 — Datenreife-Banner */}
      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">Fehler: {error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Verkauf" value={fmtEur(data?.marge?.verkauf)} sub="Kundenrechnungen Porto" loading={loading}
          Icon={TrendingUp} iconColor="#338edc" iconTint="#EBF5FD" />
        <KpiTile label="Einkauf" value={fmtEur(data?.marge?.einkauf)} sub="Carrier-/Zoll-Rechnungen" loading={loading}
          Icon={Wallet} iconColor="#EF4444" iconTint="#FEE2E2" />
        <KpiTile label="Marge" value={fmtEur(data?.marge?.db)} sub="Verkauf − Einkauf" loading={loading}
          Icon={BarChart3} iconColor="#10B981" iconTint="#D1FAE5" />
        <KpiTile label="Marge %" value={fmtPct(data?.marge?.marge_pct)}
          sub={data?.stroeme?.ohne_zoll?.marge_pct != null
            ? `ohne Zoll-Durchlauf: ${fmtPct(data.stroeme.ohne_zoll.marge_pct)}`
            : 'Marge ÷ Verkauf'}
          loading={loading}
          Icon={Percent} iconColor="#6366F1" iconTint="#E0E7FF" />
      </div>

      {/* #1789 — Getrennte Ströme: Paket / Zoll-Durchlauf / Spedition */}
      {data?.stroeme && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Ströme: Paket · Zoll-Durchlauf · Spedition</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Zuordnung per Kostenstelle (2 Paket · 18 Zoll · 20 Spedition). Der Zoll ist ein Durchlaufposten —
              er bläht Verkauf und Einkauf auf, ohne dass Marge entsteht.
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Strom</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Verkauf</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Einkauf</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Marge</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Marge %</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-28">Anteil Verkauf</th>
                </tr>
              </thead>
              <tbody>
                <StromRow label="Paket (KS 2)" s={data.stroeme.paket} />

                {/* Zoll aufklappbar — die drei Unterzeilen summieren exakt auf die Zoll-Zeile (#1791) */}
                <tr className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC] cursor-pointer"
                  onClick={() => setZollOpen(o => !o)}>
                  <td className="px-3 py-2 text-[#1A1A2E]">
                    <button type="button" className="inline-flex items-center gap-1.5 text-left"
                      aria-expanded={zollOpen}>
                      {zollOpen
                        ? <ChevronDown width={14} height={14} stroke="#6B7280" strokeWidth={2} fill="none" />
                        : <ChevronRight width={14} height={14} stroke="#6B7280" strokeWidth={2} fill="none" />}
                      Zoll (KS 18)
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.stroeme.zoll.verkauf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.stroeme.zoll.einkauf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.stroeme.zoll.db)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(data.stroeme.zoll.marge_pct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(data.stroeme.zoll.verkauf_anteil_pct)}</td>
                </tr>
                {zollOpen && data.stroeme.zoll.detail && (
                  <>
                    <ZollSubRow
                      label="davon Durchlauf (4104 ↔ 5150/5155/5160/5165)"
                      title="Weiterverrechnete Zollkosten gegen den Zollaufwand. Die Marge dieser Zeile IST das Δ der Durchlauf-Invariante."
                      s={data.stroeme.zoll.detail.durchlauf}
                      margeCell={
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          <AmpelDot ampel={data.stroeme.zoll.detail.durchlauf.ampel}
                            title={`Δ kumuliert · grün ≤ ${fmtEur(data.stroeme.zoll.ampel_schwellen?.gruen)} · rot > ${fmtEur(data.stroeme.zoll.ampel_schwellen?.gelb)}`} />
                          <span>Δ {fmtEurDetail(data.stroeme.zoll.detail.durchlauf.delta)}</span>
                        </span>
                      }
                      pctCell={data.stroeme.zoll.detail.durchlauf.delta_pct != null
                        ? `Δ ${fmtPct(data.stroeme.zoll.detail.durchlauf.delta_pct)}` : '—'}
                    />
                    <ZollSubRow
                      label="davon Gebühr (4114/4115)"
                      title="Bearbeitungsgebühr auf eigenem Konto — das einzige echte Zoll-Ergebnis."
                      s={data.stroeme.zoll.detail.gebuehr}
                      margeCell={fmtEurDetail(data.stroeme.zoll.detail.gebuehr.db)}
                      pctCell={fmtPct(data.stroeme.zoll.detail.gebuehr.marge_pct)}
                    />
                    <ZollSubRow
                      label="davon Zoll auf abweichendem Konto"
                      title="Buchungen auf Kostenstelle 18, deren Konto ausserhalb des Zoll-Kontenkreises liegt — überwiegend Konto 5101 Porto, Text „Transporte Duties & Taxes“. Wird sichtbar gemacht statt still umsortiert."
                      s={data.stroeme.zoll.detail.abweichend}
                      margeCell={fmtEurDetail(data.stroeme.zoll.detail.abweichend.db)}
                      pctCell="—"
                    />
                    {Math.abs(data.stroeme.zoll.detail_summen_diff || 0) >= 0.005 && (
                      <tr className="border-t border-[#F3F4F6] bg-red-50">
                        <td colSpan={6} className="px-3 py-2 text-xs text-red-700">
                          Unterzeilen summieren nicht auf die Zoll-Zeile: Differenz {fmtEurDetail(data.stroeme.zoll.detail_summen_diff)}
                        </td>
                      </tr>
                    )}
                  </>
                )}

                <StromRow label="Spedition (KS 20)" s={data.stroeme.spedition} />

                <tr className="border-t border-[#E5E7EB] bg-[#F8F9FC] font-semibold">
                  <td className="px-3 py-2 text-[#1A1A2E]">Gesamt</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.marge?.verkauf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.marge?.einkauf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.marge?.db)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(data.marge?.marge_pct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">100,0 %</td>
                </tr>
                <tr className="border-t border-[#F3F4F6]">
                  <td className="px-3 py-2 text-[#4B5563]">Gesamt ohne Zoll-Durchlauf</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(data.stroeme.ohne_zoll.verkauf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(data.stroeme.ohne_zoll.einkauf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(data.stroeme.ohne_zoll.db)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(data.stroeme.ohne_zoll.marge_pct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* #1791 — Δ-Prüfung des Zoll-Durchlaufs je Monat */}
      {yearTrend?.monats_trend?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">
              Zoll-Durchlauf: Δ-Prüfung je Monat {periodState.year}
            </h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Δ = weiterverrechneter Zoll (4104) − Zollaufwand (5150/5155/5160/5165). Schwellen aus der
              gemessenen Streuung: grün ≤ {fmtEur(yearTrend.ampel_schwellen?.monat?.gruen)} · gelb bis{' '}
              {fmtEur(yearTrend.ampel_schwellen?.monat?.gelb)} · darüber rot; kumuliert{' '}
              {fmtEur(yearTrend.ampel_schwellen?.kumuliert?.gruen)} / {fmtEur(yearTrend.ampel_schwellen?.kumuliert?.gelb)}.
              Δ % dient der Einordnung und steuert die Ampel nicht.
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Monat</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Weiterverrechnet</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Zollaufwand</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Δ</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Δ %</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Δ kumuliert</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Belege</th>
                </tr>
              </thead>
              <tbody>
                {yearTrend.monats_trend.map(m => {
                  const d = m.stroeme?.zoll?.detail?.durchlauf;
                  return (
                    <tr key={m.monat} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                      <td className="px-3 py-2 text-[#1A1A2E]">{MONTH_LABELS[m.monat - 1]}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(d?.verkauf)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(d?.einkauf)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">
                        <span className="inline-flex items-center gap-1.5">
                          <AmpelDot ampel={m.zoll_delta_ampel} title={`Monat: ${m.zoll_delta_ampel}`} />
                          {fmtEurDetail(m.zoll_delta)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(m.zoll_delta_pct)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">
                        <span className="inline-flex items-center gap-1.5">
                          <AmpelDot ampel={m.zoll_delta_kumuliert_ampel} title={`Kumuliert: ${m.zoll_delta_kumuliert_ampel}`} />
                          {fmtEurDetail(m.zoll_delta_kumuliert)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => setBelegeMonat(m.monat)}
                          className="text-[#338edc] hover:underline text-xs">
                          anzeigen
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t border-[#E5E7EB] bg-[#F8F9FC] font-semibold">
                  <td className="px-3 py-2 text-[#1A1A2E]">Jahr</td>
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">
                    <span className="inline-flex items-center gap-1.5">
                      <AmpelDot ampel={yearTrend.zoll_delta_jahr_ampel} title={`Jahr: ${yearTrend.zoll_delta_jahr_ampel}`} />
                      {fmtEurDetail(yearTrend.zoll_delta_jahr)}
                    </span>
                  </td>
                  <td className="px-3 py-2" />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {belegeMonat != null && (
        <ZollBelegeDrawer
          monatLabel={`${MONTH_LABELS[belegeMonat - 1]} ${periodState.year}`}
          data={belege} loading={belegeLoading} error={belegeError}
          onClose={() => setBelegeMonat(null)}
        />
      )}

      {/* Verkaufs-Standort-Tabelle */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Verkauf-Split (Kundenseite)</h2>
          {data && (
            <span className="text-[11px] text-[#9CA3AF]">
              {data.lines_processed} Buchungen · {data.period?.dateFrom} – {data.period?.dateTo}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Standort</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Verkauf</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Anteil</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Sendungen (KS#2)</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-[#9CA3AF]">Lade Odoo-Daten…</td></tr>
              )}
              {!loading && data?.standorte?.map(s => (
                <tr key={s.standort}
                  className={`border-t border-[#F3F4F6] ${s.standort === 'GESAMT' ? 'bg-[#F8F9FC] font-semibold' : 'hover:bg-[#FAFBFC]'}`}>
                  <td className="px-3 py-2 text-[#1A1A2E]">{labelFor(s.standort)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(s.umsatz)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(s.umsatz_anteil_pct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtInt(s.sendungen_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stück-KPIs */}
      {data?.marge && data.marge.sendungen_count > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Marge pro Sendung — nur Paketstrom (KS 2)</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Sendungen = Σ <code>unit_amount</code> auf invoice-Lines auf Kostenstelle #2 (Versand).
              {' '}Zoll (KS 18) und Spedition (KS 20) haben keinen Sendungs-Nenner und fließen NICHT ein (#1789).
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <StueckKpiTile label="Sendungen" value={fmtInt(data.marge.sendungen_count)} sub="Σ unit_amount KS#2" />
            <StueckKpiTile label="Verkauf / Sendung" value={fmtEurCent(data.marge.verkauf_per_sendung)} sub="nur Paketstrom" />
            <StueckKpiTile label="Einkauf / Sendung" value={fmtEurCent(data.marge.einkauf_per_sendung)} sub="nur Paketstrom" />
            <StueckKpiTile label="Marge / Sendung" value={fmtEurCent(data.marge.marge_per_sendung)} sub="nur Paketstrom" />
          </div>
        </div>
      )}

      {/* Top-5 Carrier */}
      {data?.top_carrier && !data.top_carrier.error && data.top_carrier.top5?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Top-5 Carrier (Einkauf)</h2>
            <span className="text-[11px] text-[#9CA3AF]">
              {data.top_carrier.carrier_count} Carrier gesamt · Σ Einkauf {fmtEurDetail(data.top_carrier.total_einkauf)}
              {data.top_carrier.nicht_zugeordnet != null && Math.abs(data.top_carrier.nicht_zugeordnet) >= 0.01 && (
                <> · davon {fmtEurDetail(data.top_carrier.nicht_zugeordnet)} Abgrenzungen/ohne Partner nicht in der Carrier-Liste</>
              )}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Carrier</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Einkauf</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {data.top_carrier.top5.map((r, i) => (
                  <tr key={r.partner_id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                    <td className="px-3 py-2 text-[#9CA3AF]">{i + 1}</td>
                    <td className="px-3 py-2 text-[#1A1A2E]">{r.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(r.einkauf)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(r.anteil_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monats-Trend */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Monats-Trend {periodState.year}</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Verkauf · Einkauf · Marge — nur Monate mit Carrier-Buchungen.
            </div>
          </div>
          {yearTrendError && <span className="text-[11px] text-red-600">Fehler: {yearTrendError}</span>}
        </div>
        <div className="p-4">
          {yearTrendLoading && !yearTrend && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Lade Jahres-Trend…</div>
          )}
          {!yearTrendLoading && chartData.length === 0 && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Keine Daten für {periodState.year}.</div>
          )}
          {chartData.length > 0 && reifeFooter(periodState.year) && (
            <div className="text-[11px] text-[#9CA3AF] mb-2">
              {reifeFooter(periodState.year)}
            </div>
          )}
          {chartData.length > 0 && (
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="monat" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
                    tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'} />
                  <Tooltip formatter={v => fmtEurDetail(v)}
                    labelStyle={{ color: '#1A1A2E', fontWeight: 600 }}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                  {/* #1184 v2.569.0 — Vorläufige Monate markieren */}
                  {MaturityOverlay({ year: periodState.year, monthLabels: MONTH_LABELS })}
                  <Line type="monotone" dataKey="Verkauf" stroke="#338edc" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Einkauf" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Marge" stroke="#10B981" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-[#9CA3AF]">
        Quelle: Odoo <code>account.analytic.line</code> · Kostenstellen Versand (2), Zoll (18), Spedition (20)
        UND fachlich freigegebener Kontenkreis (Mapping #1789) · nur gebuchte Belege (<code>posted</code>) ·
        Marge = Σ <code>invoice</code> − Σ (<code>vendor_bill</code> + <code>other</code>).
        Sendungs-Mengen via <code>unit_amount</code> auf invoice-Lines KS#2.
      </div>
    </div>
  );
}

function StromRow({ label, s }) {
  return (
    <tr className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
      <td className="px-3 py-2 text-[#1A1A2E]">{label}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(s.verkauf)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(s.einkauf)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(s.db)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(s.marge_pct)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(s.verkauf_anteil_pct)}</td>
    </tr>
  );
}

function ZollSubRow({ label, title, s, margeCell, pctCell }) {
  return (
    <tr className="border-t border-[#F3F4F6] bg-[#FAFBFC]" title={title}>
      <td className="px-3 py-2 pl-9 text-[#4B5563]">{label}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.verkauf)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.einkauf)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{margeCell}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{pctCell}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[#9CA3AF]">—</td>
    </tr>
  );
}

const GRUPPE_LABEL = { durchlauf: 'Durchlauf', gebuehr: 'Gebühr', abweichend: 'abw. Konto' };

/** Klickpfad zur Δ-Ampel — zeigt die Belege, statt sie zu erklären (#1791). */
function ZollBelegeDrawer({ monatLabel, data, loading, error, onClose }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Zoll-Buchungen {monatLabel}</h2>
          {data?.summen && (
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Weiterverrechnet {fmtEurDetail(data.summen.durchlauf_erloes)} · Zollaufwand {fmtEurDetail(data.summen.zollaufwand)} ·
              Δ {fmtEurDetail(data.summen.delta)} · Gebühr {fmtEurDetail(data.summen.gebuehr)} ·
              abw. Konto {fmtEurDetail(data.summen.abweichend)}
              {data.summen.eust_zeilen > 0 && (
                <> · davon EUSt {fmtEurDetail(data.summen.eust)} in {data.summen.eust_zeilen} Zeilen</>
              )}
            </div>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Schließen"
          className="shrink-0 p-1 rounded hover:bg-[#F3F4F6]">
          <X width={16} height={16} stroke="#6B7280" strokeWidth={2} fill="none" />
        </button>
      </div>
      {loading && <div className="px-4 py-6 text-sm text-[#9CA3AF]">Lade Belege…</div>}
      {error && <div className="px-4 py-3 text-sm text-red-700 bg-red-50">Fehler: {error}</div>}
      {data?.belege?.length === 0 && !loading && (
        <div className="px-4 py-6 text-sm text-[#9CA3AF]">Keine Zoll-Buchungen in diesem Monat.</div>
      )}
      {data?.belege?.length > 0 && (
        <div className="overflow-x-auto max-h-[520px]">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Datum</th>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Beleg</th>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Partner</th>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Konto</th>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-28">Gruppe</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {data.belege.map(b => (
                <tr key={b.id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC] align-top">
                  <td className="px-3 py-2 text-[#4B5563] tabular-nums whitespace-nowrap">{b.datum}</td>
                  <td className="px-3 py-2 text-[#1A1A2E]">
                    {b.odoo_link ? (
                      <a href={b.odoo_link} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#338edc] hover:underline">
                        {b.beleg}
                        <ExternalLink width={12} height={12} stroke="currentColor" strokeWidth={2} fill="none" />
                      </a>
                    ) : (
                      <span className="text-[#9CA3AF]">ohne Beleg</span>
                    )}
                    {b.text && <div className="text-[11px] text-[#9CA3AF] mt-0.5">{b.text}</div>}
                  </td>
                  <td className="px-3 py-2 text-[#4B5563]">{b.partner || '—'}</td>
                  <td className="px-3 py-2 text-[#4B5563] tabular-nums" title={b.konto_name || ''}>{b.konto_code || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="text-[11px] text-[#6B7280]">{GRUPPE_LABEL[b.gruppe] || b.gruppe}</span>
                    {b.ist_eust && (
                      <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FEF3C7] text-[#92400E]">
                        EUSt
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E] whitespace-nowrap">{fmtEurDetail(b.betrag)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function labelFor(s) {
  if (s === 'AT') return 'Österreich';
  if (s === 'DE') return 'Deutschland';
  if (s === 'GESAMT') return 'Gesamt';
  return s;
}

function KpiTile({ label, value, sub, loading, Icon, iconColor, iconTint }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
        {Icon && (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: iconTint || '#F3F4F6' }} aria-hidden="true">
            <Icon width={16} height={16} stroke={iconColor || '#6B7280'} strokeWidth={2} fill="none" />
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums">
        {loading ? <span className="inline-block w-24 h-7 rounded bg-[#F3F4F6] animate-pulse" /> : value}
      </div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}

function StueckKpiTile({ label, value, sub }) {
  return (
    <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}
